import { useCallback, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Box } from '@/shared/ui';

import ReadOnlyIcon from '@/assets/icons/ReadOnlyIcon';
import { dashboardOptions } from '@/features/dashboard/api/dashboard.options';
import {
  buildDashboardPath,
  commitConfirmedDashboardConfig,
  SAVE_SNACKBAR_KEY,
  saveDashboardToApi
} from '@/features/dashboard/api/mutations';
import { useDashboard, useSharedDashboard } from '@/features/dashboard/api/queries';
import { DashboardLayout } from '@/features/dashboard/components/DashboardLayout';
import {
  SHARE_TOKEN_PARAM,
  SHARED_DASHBOARD_EXPIRED_PATH,
  SHARED_DASHBOARD_NOT_FOUND_PATH
} from '@/features/dashboard/constants/const';
import { extractFirstDashboard } from '@/features/dashboard/lib/extractFirstDashboard';
import { getTabContent } from '@/features/dashboard/lib/transformers';
import type { DashboardTab } from '@/features/dashboard/types';
import { MarketsComponents, MarketsSkeleton, useMarketPreview } from '@/features/markets';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';
import { getUniqueName } from '@/shared/lib/format/getUniqueName';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';
import Typography from '@/shared/ui/typography/Text';

import { clock } from '@/services/clock';
import { SharedDashboardBanner } from './SharedDashboardBanner';

interface ShareTokenPayload {
  dashboardName: string;
  dashboardSharer: string;
  exp: number;
  iat: number;
  iss: string;
}

const ReadOnlyBox = () => (
  <Box
    sx={{
      height: '48px',
      backgroundColor: 'gx.tileLight',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 1,
      px: '12px'
    }}
  >
    <ReadOnlyIcon />
    <Typography>Read only</Typography>
  </Box>
);

type SaveSharedDashboardVariables = {
  saveName: string;
  tabs: DashboardTab;
};

const SharedDashboard = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(SHARE_TOKEN_PARAM);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showLoadingSnackbar, showSuccessSnackbar, showErrorSnackbar } =
    useSnackbarNotifications();
  const [activeTab, setActiveTab] = useState(0);

  const { data: dashboards } = useDashboard();
  const {
    data: sharedDashboardData,
    isLoading: isSharedLoading,
    isError: isSharedError
  } = useSharedDashboard(token);

  const { dashboardName: sharedName, dashboardTabs } = useMemo(
    () => extractFirstDashboard(sharedDashboardData),
    [sharedDashboardData]
  );

  const tabNames = useMemo(
    () => (dashboardTabs ? Object.keys(dashboardTabs) : []),
    [dashboardTabs]
  );

  const activeTabConfig = useMemo(() => {
    if (!dashboardTabs || tabNames.length === 0) return undefined;
    const tabKey = tabNames[activeTab];
    if (!tabKey) return undefined;
    return getTabContent(dashboardTabs[tabKey]);
  }, [dashboardTabs, tabNames, activeTab]);

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching
  } = useMarketPreview(activeTabConfig, !!activeTabConfig);

  const isLoading = isSharedLoading || isPreviewLoading || isPreviewFetching;

  const handleTabChange = useCallback((tabIndex: number) => {
    setActiveTab(tabIndex);
  }, []);

  const { mutate: saveDashboard, isPending: isSaving } = useMutation({
    mutationKey: dashboardOptions.mutations.save(),
    mutationFn: async ({ saveName, tabs }: SaveSharedDashboardVariables) => {
      const confirmedDashboards = await saveDashboardToApi(saveName, tabs);
      return { saveName, confirmedDashboards };
    },
    onSuccess: ({ saveName, confirmedDashboards }) => {
      commitConfirmedDashboardConfig(queryClient, confirmedDashboards);
      showSuccessSnackbar('Dashboard saved!', SAVE_SNACKBAR_KEY);
      void navigate(buildDashboardPath(saveName));
    },
    onError: () => {
      showErrorSnackbar("Oops! We couldn't save the dashboard. Please try again.");
    }
  });

  const handleSave = () => {
    if (!dashboardTabs || !dashboards) return;

    showLoadingSnackbar('Saving dashboard, please wait...', SAVE_SNACKBAR_KEY);
    const existingNames = Object.keys(dashboards);
    const saveName = getUniqueName(existingNames, sharedName ?? 'Shared Dashboard');
    saveDashboard({ saveName, tabs: dashboardTabs });
  };

  if (isSharedError) {
    return (
      <Navigate
        replace
        to={SHARED_DASHBOARD_NOT_FOUND_PATH}
      />
    );
  }

  if (!token) {
    return (
      <Navigate
        replace
        to='/notfound'
      />
    );
  }

  let decoded: ShareTokenPayload;
  try {
    decoded = jwtDecode<ShareTokenPayload>(token);
  } catch {
    return (
      <Navigate
        replace
        to='/notfound'
      />
    );
  }

  if (decoded.exp * 1000 < clock.now()) {
    return (
      <Navigate
        replace
        to={SHARED_DASHBOARD_EXPIRED_PATH}
      />
    );
  }

  const { dashboardName: name, dashboardSharer: user } = decoded;

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <SharedDashboardBanner
        dashboardName={name}
        isLoading={isLoading}
        isSaving={isSaving}
        user={user}
        onSave={handleSave}
      />
      <DashboardLayout
        activeTab={activeTab}
        renderRight={<ReadOnlyBox />}
        stickyHeader={false}
        tabNames={tabNames}
        title={sharedName ?? name}
        onTabChange={handleTabChange}
      >
        {isLoading ? (
          <MarketsSkeleton />
        ) : (
          activeTabConfig &&
          activeTabConfig.length > 0 &&
          previewData && (
            <MarketsComponents
              components={previewData.components}
              showAddToDashboard={false}
              showShareBlock={false}
            />
          )
        )}
      </DashboardLayout>
    </PageInformationContext>
  );
};

export default SharedDashboard;
